import { t } from "i18next";
import { ValidationFunction } from "./types";
import dayjs from "dayjs";

export const isRequiredIf: ValidationFunction = (value: any, args: any[]) => {
  if (!args) {
    console.error("Invalid arguments passed to isRequiredIf:", args);
    return null;
  }
  if (args[0] !== args[1]) {
    return null;
  }

  if (args[0] === args[1]) {
    let val = value;
    if (Array.isArray(value) && "blocks" in value) {
      val = value.blocks;
    }
    const check = isNotNullOrEmpty(val, typeof val);
    if (check !== null) {
      return check;
    } else {
      return null;
    }
  }
  return null;
};

export const isDisabledIfNew: ValidationFunction = (
  value: any,
  target: any
) => {
  if (!target) {
    console.error("Invalid argument passed to isDisabledIfNew:", target);
    return null;
  }

  if (value === target) {
    return null;
  }

  return {
    error: "isDisabledIfNew",
    message: t("forms.validation.is_new_form"),
  };
};

export const isGreaterThan: ValidationFunction = (
  value: number,
  target: number
) => {
  if (value > target) {
    return null;
  }
  return {
    error: "isGreaterThan",
    message: t("forms.validation.is_greater_than", { target }),
  };
};

export const isNotOneOf: ValidationFunction = (
  value: string,
  target: string[]
) => {
  if (!target.includes(value)) {
    return null;
  }
  return {
    error: "isNotOneOf",
    message: t("forms.validation.is_not_one_of", {
      target: target.join(", "),
    }),
  };
};

export const isLessThan: ValidationFunction = (
  value: number,
  target: number
) => {
  if (value < target) {
    return null;
  }
  return {
    error: "isLessThan",
    message: t("forms.validation.is_less_than", { target }),
  };
};

export const isGreaterThanOrEqualTo: ValidationFunction = (
  value: number,
  target: number
) => {
  if (value >= target) {
    return null;
  }
  return {
    error: "isGreaterThanOrEqualTo",
    message: t("forms.validation.is_greater_than_or_equal_to", { target }),
  };
};

export const isLessThanOrEqualTo: ValidationFunction = (
  value: number,
  target: number
) => {
  if (value <= target) {
    return null;
  }
  return {
    error: "isLessThanOrEqualTo",
    message: t("forms.validation.is_less_than_or_equal_to", { target }),
  };
};

export const isNotEqualTo: ValidationFunction = (
  value: number,
  target: number
) => {
  if (value !== target) {
    return null;
  }
  return {
    error: "isNotEqualTo",
    message: t("forms.validation.is_not_equal_to", { target }),
  };
};

export const isEqualTo: ValidationFunction = (
  value: number,
  target: number
) => {
  if (value === target) {
    return null;
  }
  return {
    error: "isEqualTo",
    message: t("forms.validation.is_equal_to", { target }),
  };
};

export const isNotNullOrEmpty: ValidationFunction = (
  value: any,
  type: string
) => {
  let val = value;
  if (value && Array.isArray(value.blocks) && "blocks" in value) {
    val = value.blocks;
  }
  if (
    val !== null &&
    val !== undefined &&
    ((type === "string" && val.trim() !== "") ||
      (type === "array" && Array.isArray(val) && val.length > 0) ||
      (type === "number" && !isNaN(val)) ||
      (type === "object" && Object.keys(val).length > 0) ||
      (type === "boolean" && val === true))
  ) {
    return null;
  }
  return {
    error: "isNotNullOrEmpty",
    message: t("forms.validation.is_not_null_or_empty"),
  };
};

export const isEmpty: ValidationFunction = (value: Array<any>) => {
  if (value.length > 0) {
    return null;
  }
  return {
    error: "isEmpty",
    message: t("forms.validation.is_empty"),
  };
};

export const hasMinimumLength: ValidationFunction = (
  value: Array<any>,
  target: number
) => {
  if (value && value.length >= target) {
    return null;
  }
  return {
    error: "hasMinimumLength",
    message: t("forms.validation.has_minimum_length", { target }),
  };
};

export const hasMaximumLength: ValidationFunction = (
  value: Array<any>,
  target: number
) => {
  if (value && value.length <= target) {
    return null;
  }
  return {
    error: "hasMaximumLength",
    message: t("forms.validation.has_maximum_length", { target }),
  };
};

export const isBefore: ValidationFunction = (
  value: Date,
  target: any | null | undefined
) => {
  if (target && Array.isArray(target) && target.includes(null)) {
    return null;
  }

  if (value && dayjs(value).isBefore(dayjs(target[1]), "day")) {
    return null;
  }
  return {
    error: "isBefore",
    message: t("forms.validation.is_before", {
      target: dayjs(target[1]).format("YYYY-MM-DD"),
    }),
  };
};

export const isSameOrBefore: ValidationFunction = (
  value: Date,
  target: any | null | undefined
) => {
  if (target && Array.isArray(target) && target.includes(null)) {
    return null;
  }

  if (
    value &&
    target &&
    (dayjs(value).isBefore(target[1]) || dayjs(value).isSame(target[1], "day"))
  ) {
    return null;
  }

  return {
    error: "isSameOrBefore",
    message: t("forms.validation.is_same_or_before", {
      target: dayjs(target[1]).format("YYYY-MM-DD"),
    }),
  };
};

export const isAfter: ValidationFunction = (
  value: Date,
  target: any | null | undefined
) => {
  if (target && Array.isArray(target) && target.includes(null)) {
    return null;
  }

  if (value && target && dayjs(value).isAfter(target[1], "day")) {
    return null;
  }
  return {
    error: "isAfter",
    message: t("forms.validation.is_after", {
      target: dayjs(target[1]).format("YYYY-MM-DD"),
    }),
  };
};

export const isSameOrAfter: ValidationFunction = (
  value: Date,
  target: any | null | undefined
) => {
  if (target && Array.isArray(target) && target.includes(null)) {
    return null;
  }

  if (
    value &&
    target &&
    (dayjs(value).isAfter(target[1]) || dayjs(value).isSame(target[1], "day"))
  ) {
    return null;
  }
  return {
    error: "isSameOrAfter",
    message: t("forms.validation.is_same_or_after", {
      target: dayjs(target[1]).format("YYYY-MM-DD"),
    }),
  };
};

export const isNotInFuture: ValidationFunction = (value: Date) => {
  if (!value || (value && dayjs().isSameOrAfter(value, "day"))) {
    return null;
  }
  return {
    error: "isNotInFuture",
    message: t("forms.validation.is_not_in_future"),
  };
};

export const isNonNegative: ValidationFunction = (
  value: number | string,
  type?: string
) => {
  let val = value;
  if (typeof value === "string") {
    val = value.split("-")[0];
  }
  if (Number(val) >= 0) {
    return null;
  }
  return {
    error: "isNonNegative",
    message: t("forms.validation.is_not_null_or_empty"),
  };
};

export const validationRegistry: { [key: string]: ValidationFunction } = {
  isRequiredIf,
  isGreaterThan,
  isNotOneOf,
  isLessThan,
  isGreaterThanOrEqualTo,
  isLessThanOrEqualTo,
  isNotEqualTo,
  isEqualTo,
  isNotNullOrEmpty,
  isEmpty,
  hasMinimumLength,
  hasMaximumLength,
  isBefore,
  isSameOrBefore,
  isAfter,
  isSameOrAfter,
  isNonNegative,
  isNotInFuture,
  isDisabledIfNew,
};

export const getValidationFunction = (name: string): ValidationFunction => {
  return validationRegistry[name];
};
